import React, { useRef, useEffect, useState, useMemo } from 'react';
import * as d3 from 'd3';

const MARGIN = { top: 40, right: 30, bottom: 30, left: 50 };
const NODE_WIDTH = 20;
const NODE_PADDING = 10;
const NODE_RADIUS = 3;

const QUARTILE_COLORS = {
  Q1: '#2563eb',
  Q2: '#16a34a',
  Q3: '#f59e0b',
  Q4: '#dc2626',
};

const QUARTILE_LABELS = ['Q1', 'Q2', 'Q3', 'Q4'];

const styles = {
  card: {
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '24px',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  title: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#1f2937',
    marginBottom: '4px',
  },
  subtitle: {
    fontSize: '12px',
    color: '#9ca3af',
    marginBottom: '16px',
  },
  container: {
    overflowX: 'auto',
  },
  tooltip: {
    position: 'fixed',
    pointerEvents: 'none',
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    padding: '8px 12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
    fontSize: '12px',
    zIndex: 1000,
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  stabilityBar: {
    marginTop: '16px',
    padding: '12px 16px',
    background: '#f9fafb',
    borderRadius: '6px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '13px',
    color: '#374151',
  },
};

function formatDateLabel(dateStr) {
  const date = new Date(dateStr);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[date.getMonth()]} ${date.getDate()}`;
}

export default function PriceQuartileFlow({ data }) {
  const svgRef = useRef(null);
  const containerRef = useRef(null);
  const [tooltip, setTooltip] = useState(null);

  const stabilityPct = useMemo(() => {
    if (!data || !data.flows || data.flows.length === 0 || !data.totalAddresses) return null;
    const totalFlowCount = data.flows.reduce((sum, f) => sum + f.count, 0);
    if (totalFlowCount === 0) return null;
    const sameQuartileCount = data.flows
      .filter((f) => f.from.quartile === f.to.quartile)
      .reduce((sum, f) => sum + f.count, 0);
    return ((sameQuartileCount / totalFlowCount) * 100).toFixed(1);
  }, [data]);

  useEffect(() => {
    if (!data || !data.periods || data.periods.length < 2 || !data.flows || data.flows.length === 0) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const { periods, flows, totalAddresses } = data;
    const numColumns = periods.length;

    // Responsive width from container, with viewBox for scaling
    const viewWidth = 700;
    const viewHeight = 400;

    svg
      .attr('width', '100%')
      .attr('viewBox', `0 0 ${viewWidth} ${viewHeight}`)
      .attr('preserveAspectRatio', 'xMidYMid meet');

    const innerW = viewWidth - MARGIN.left - MARGIN.right;
    const innerH = viewHeight - MARGIN.top - MARGIN.bottom;

    const g = svg.append('g').attr('transform', `translate(${MARGIN.left}, ${MARGIN.top})`);

    // Column x positions
    const columnX = [];
    for (let i = 0; i < numColumns; i++) {
      columnX.push(numColumns === 1 ? innerW / 2 : (i / (numColumns - 1)) * (innerW - NODE_WIDTH));
    }

    // Build node data: for each period, compute quartile block positions
    // Quartiles ordered Q1 (bottom) to Q4 (top)
    const columnNodes = periods.map((period, colIdx) => {
      const quartiles = period.quartiles || {};
      const totalCount = QUARTILE_LABELS.reduce((sum, q) => sum + (quartiles[q] || 0), 0);
      if (totalCount === 0) return [];

      // Layout Q4 at top, Q1 at bottom
      const orderedQuartiles = [...QUARTILE_LABELS].reverse(); // Q4, Q3, Q2, Q1
      const activeQuartiles = orderedQuartiles.filter((q) => (quartiles[q] || 0) > 0);
      const totalPadding = Math.max(0, (activeQuartiles.length - 1)) * NODE_PADDING;
      const drawableHeight = innerH - totalPadding;

      const nodes = [];
      let y = 0;
      activeQuartiles.forEach((q, i) => {
        const count = quartiles[q] || 0;
        const height = Math.max((count / totalCount) * drawableHeight, 12);
        nodes.push({
          quartile: q,
          count,
          y,
          height,
          color: QUARTILE_COLORS[q],
          periodIdx: colIdx,
          date: period.date,
          totalCount,
        });
        y += height + NODE_PADDING;
      });

      // Normalize if total exceeds innerH
      const totalUsed = nodes.reduce((s, n) => s + n.height, 0) + totalPadding;
      if (totalUsed > innerH) {
        const scale = (innerH - totalPadding) / nodes.reduce((s, n) => s + n.height, 0);
        let yy = 0;
        nodes.forEach((n) => {
          n.height = Math.max(n.height * scale, 8);
          n.y = yy;
          yy += n.height + NODE_PADDING;
        });
      }

      return nodes;
    });

    // Draw period date labels along the top
    periods.forEach((period, i) => {
      g.append('text')
        .attr('x', columnX[i] + NODE_WIDTH / 2)
        .attr('y', -14)
        .attr('text-anchor', 'middle')
        .attr('font-size', '11px')
        .attr('font-weight', '600')
        .attr('fill', '#6b7280')
        .text(formatDateLabel(period.date));
    });

    // Draw quartile labels on the left side
    // Use the first column's node positions as reference
    if (columnNodes[0] && columnNodes[0].length > 0) {
      columnNodes[0].forEach((node) => {
        g.append('text')
          .attr('x', -10)
          .attr('y', node.y + node.height / 2)
          .attr('dy', '0.35em')
          .attr('text-anchor', 'end')
          .attr('font-size', '11px')
          .attr('font-weight', '600')
          .attr('fill', QUARTILE_COLORS[node.quartile])
          .text(node.quartile);
      });
    }

    // Helper: find node center-y for a given column and quartile
    function findNode(colIdx, quartile) {
      return (columnNodes[colIdx] || []).find((n) => n.quartile === quartile);
    }

    // Draw flows between adjacent periods
    // First, group flows by (fromPeriodIdx, toPeriodIdx) pair and compute offsets
    const flowsByPair = {};
    flows.forEach((flow) => {
      const fromIdx = periods.findIndex((p) => p.date === flow.from.period);
      const toIdx = periods.findIndex((p) => p.date === flow.to.period);
      if (fromIdx === -1 || toIdx === -1) return;
      const key = `${fromIdx}-${toIdx}`;
      if (!flowsByPair[key]) flowsByPair[key] = [];
      flowsByPair[key].push({ ...flow, fromIdx, toIdx });
    });

    // Track offsets per node for stacking flow ribbons
    const srcOffsets = {};
    const tgtOffsets = {};

    Object.values(flowsByPair).forEach((pairFlows) => {
      pairFlows.forEach((flow) => {
        const srcNode = findNode(flow.fromIdx, flow.from.quartile);
        const tgtNode = findNode(flow.toIdx, flow.to.quartile);
        if (!srcNode || !tgtNode || flow.count === 0) return;

        const srcKey = `${flow.fromIdx}-${flow.from.quartile}`;
        const tgtKey = `${flow.toIdx}-${flow.to.quartile}`;
        if (srcOffsets[srcKey] == null) srcOffsets[srcKey] = 0;
        if (tgtOffsets[tgtKey] == null) tgtOffsets[tgtKey] = 0;

        const srcLinkHeight = srcNode.count > 0
          ? (flow.count / srcNode.count) * srcNode.height
          : 0;
        const tgtLinkHeight = tgtNode.count > 0
          ? (flow.count / tgtNode.count) * tgtNode.height
          : 0;

        if (srcLinkHeight < 0.5 && tgtLinkHeight < 0.5) return;

        const srcY0 = srcNode.y + srcOffsets[srcKey];
        const srcY1 = srcY0 + srcLinkHeight;
        const tgtY0 = tgtNode.y + tgtOffsets[tgtKey];
        const tgtY1 = tgtY0 + tgtLinkHeight;

        srcOffsets[srcKey] += srcLinkHeight;
        tgtOffsets[tgtKey] += tgtLinkHeight;

        const x0 = columnX[flow.fromIdx] + NODE_WIDTH;
        const x1 = columnX[flow.toIdx];
        const midX = (x0 + x1) / 2;

        const pathData = `
          M ${x0},${srcY0}
          C ${midX},${srcY0} ${midX},${tgtY0} ${x1},${tgtY0}
          L ${x1},${tgtY1}
          C ${midX},${tgtY1} ${midX},${srcY1} ${x0},${srcY1}
          Z
        `;

        const isSame = flow.from.quartile === flow.to.quartile;
        const baseOpacity = isSame ? 0.15 : 0.35;
        const hoverOpacity = 0.65;

        const fromDate = flow.from.period;
        const toDate = flow.to.period;

        g.append('path')
          .attr('d', pathData)
          .attr('fill', QUARTILE_COLORS[flow.from.quartile])
          .attr('opacity', baseOpacity)
          .attr('stroke', 'none')
          .style('cursor', 'pointer')
          .on('mouseenter', function (event) {
            d3.select(this).attr('opacity', hoverOpacity);
            const message = `${flow.count} address${flow.count !== 1 ? 'es' : ''} moved from ${flow.from.quartile} to ${flow.to.quartile} between ${formatDateLabel(fromDate)} and ${formatDateLabel(toDate)}`;
            setTooltip({
              x: event.clientX + 12,
              y: event.clientY - 10,
              message,
            });
          })
          .on('mousemove', function (event) {
            setTooltip((prev) => prev ? { ...prev, x: event.clientX + 12, y: event.clientY - 10 } : prev);
          })
          .on('mouseleave', function () {
            d3.select(this).attr('opacity', baseOpacity);
            setTooltip(null);
          });
      });
    });

    // Draw nodes (blocks) on top of flows
    columnNodes.forEach((nodes, colIdx) => {
      nodes.forEach((node) => {
        g.append('rect')
          .attr('x', columnX[colIdx])
          .attr('y', node.y)
          .attr('width', NODE_WIDTH)
          .attr('height', node.height)
          .attr('rx', NODE_RADIUS)
          .attr('ry', NODE_RADIUS)
          .attr('fill', node.color)
          .attr('stroke', '#fff')
          .attr('stroke-width', 0.5)
          .style('cursor', 'pointer')
          .on('mouseenter', function (event) {
            d3.select(this).attr('stroke', '#1f2937').attr('stroke-width', 1.5);
            const pct = totalAddresses > 0
              ? ((node.count / totalAddresses) * 100).toFixed(1)
              : '0.0';
            setTooltip({
              x: event.clientX + 12,
              y: event.clientY - 10,
              message: `${node.quartile} — ${formatDateLabel(node.date)}`,
              detail: `${node.count} address${node.count !== 1 ? 'es' : ''} (${pct}% of total)`,
            });
          })
          .on('mousemove', function (event) {
            setTooltip((prev) => prev ? { ...prev, x: event.clientX + 12, y: event.clientY - 10 } : prev);
          })
          .on('mouseleave', function () {
            d3.select(this).attr('stroke', '#fff').attr('stroke-width', 0.5);
            setTooltip(null);
          });

        // Count label inside or next to block if tall enough
        if (node.height >= 16) {
          const isLast = colIdx === numColumns - 1;
          const labelX = isLast
            ? columnX[colIdx] - 4
            : columnX[colIdx] + NODE_WIDTH + 4;
          const labelAnchor = isLast ? 'end' : 'start';

          g.append('text')
            .attr('x', labelX)
            .attr('y', node.y + node.height / 2)
            .attr('dy', '0.35em')
            .attr('text-anchor', labelAnchor)
            .attr('font-size', '10px')
            .attr('fill', '#374151')
            .attr('font-weight', '500')
            .text(node.count);
        }
      });
    });

    return () => {
      svg.selectAll('*').remove();
    };
  }, [data]);

  // Empty state
  if (!data || !data.periods || data.periods.length < 2 || !data.flows || data.flows.length === 0) {
    return (
      <div style={styles.card}>
        <div style={styles.title}>Price Quartile Migration</div>
        <div style={styles.subtitle}>How addresses move between price quartiles over time</div>
        <div style={{ color: '#6b7280', textAlign: 'center', padding: '40px 0' }}>
          No data available
        </div>
      </div>
    );
  }

  return (
    <div style={styles.card}>
      <div style={styles.title}>Price Quartile Migration</div>
      <div style={styles.subtitle}>How addresses move between price quartiles over time</div>
      <div style={styles.container} ref={containerRef}>
        <svg ref={svgRef} />
      </div>
      {stabilityPct !== null && (
        <div style={styles.stabilityBar}>
          <span style={{ fontWeight: 600, color: '#2563eb' }}>{stabilityPct}%</span>
          <span>of addresses remained in the same quartile</span>
        </div>
      )}
      {tooltip && (
        <div
          style={{
            ...styles.tooltip,
            left: tooltip.x,
            top: tooltip.y,
          }}
        >
          <div style={{ fontWeight: 600, color: '#1f2937' }}>{tooltip.message}</div>
          {tooltip.detail && (
            <div style={{ color: '#6b7280', marginTop: '2px' }}>{tooltip.detail}</div>
          )}
        </div>
      )}
    </div>
  );
}
